// 마을 퀘스트 추적기(설계도 ui-village-quest 시안 C5의 후속, 대표님 2026-10-24 "왼쪽 퀘스트 창은 항상 저렇게 있으니까 매우 불편하다").
// 예전의 상시 펼친 카드(지역 이름·북쪽 두 문·워프·다음 목표, 화면의 약 12~14%)를 작은 추적기로 줄였다. 두 모드:
//   · min  = 아이콘 하나(44px). 폰 기본(짧은 변 < 500px). 누르면 한 줄 추적기로 펴진다.
//   · line = 한 줄 추적기: 목표 이름 + 수치/설명 + 「자동」 + 접기. PC·넓은 화면 기본. 글자 부분을 누르면 퀘스트 창(questWindow.ts)이 열린다.
// 지역 길·워프·일일 의뢰·목표 상세·보상은 퀘스트 창으로 옮겼다(기능 그대로, 창에서 「자동 이동」·워프를 쓴다).
// 추적기가 차지한 사각형을 villageHudLayout.rect/bottom으로 알려 골드·가방 줄과 터치 조이스틱 영역(touch.ts)이 그것을 피한다.
// 접힘 모드는 기기 전용 저장(questCardModel.ts, localStorage 'dungeonhero.ui.questCard').
import { autoActive } from '../shared/autoRoute';
import { assetUrl } from '../pwa/assetMap';
import { elementRect, setPanelRect } from '../shared/panelRects';
import { viewW, viewH } from '../shared/viewport';
import type { AdventureView } from '../shared/adventure';
import { QUEST_TAB_EVENT } from './uiEvents';
import { AUTO_QUEST_EVENT, type AutoRequest } from './autoQuest';
import { C, FONT, el } from './dom';
import {
  CARD_LEFT, MIN_BODY_PX, MIN_BUTTON, QUEST_CARD_EVENT, bountyChipText, bountyStatus, cardTopPx, chipHeight, type BountyStatus, loadQuestCardPrefs, newTrackerLayout, resolveTrackerMode, toggleTracker,
  trackerLayout, updateQuestCardPrefs, villageHudLayout, type QuestCardPrefs, type TrackerLayout, type TrackerMode,
} from './questCardModel';
import { iconOver, regionEmblem, setUiIcon, townCrest } from './uiArt';
import type { QuestAction, QuestGoalView } from './villageQuestModel';
import { TIER_STYLE } from './regionCard';
import { EQUIP_BEST_EVENT, equipTick } from './equipFeedbackModel';
import { installEquipFxStyle } from './equipFeedback';

export { villageHudLayout };

const SHADOW = 'text-shadow:0 1px 0 #000,0 2px 4px rgba(0,0,0,.75);';
// 금색 나침반 별과 둥근 받침(마을 문장 그림을 받기 전·실패했을 때)
const STAR_SVG = '<svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">' +
  '<defs><radialGradient id="dhq-sg" cx="40%" cy="35%"><stop offset="0" stop-color="#fff6c8"/><stop offset=".5" stop-color="#ffd76a"/><stop offset="1" stop-color="#b8741c"/></radialGradient></defs>' +
  '<circle cx="20" cy="20" r="18.5" fill="#0d1328" stroke="#c99a3a" stroke-width="1.5"/>' +
  '<path d="M20 3.5 23 17 36.5 20 23 23 20 36.5 17 23 3.5 20 17 17Z" fill="url(#dhq-sg)"/>' +
  '<path d="M20 9.5 21.6 18.4 30.5 20 21.6 21.6 20 30.5 18.4 21.6 9.5 20 18.4 18.4Z" transform="rotate(45 20 20)" fill="#f2c14e" opacity=".85"/>' +
  '<circle cx="20" cy="20" r="2.2" fill="#fff6d0"/></svg>';
const GATE_COLOR = { easy: '#7ff0a8', hard: '#ffb45e' } as const;
// 성문(아치) 그림: 지역 난이도 색으로 칠한다
const gateSvg = (color: string, px: number): string => '<svg viewBox="0 0 24 24" width="' + px + '" height="' + px + '" aria-hidden="true"><path fill="' + color + '" d="M3 21V9l2-1V5h3v2h2V4h4v3h2V5h3v3l2 1v12h-7v-5a2 2 0 0 0-4 0v5Z"/>' +
  '<path fill="rgba(0,0,0,.35)" d="M10 21v-5a2 2 0 0 1 4 0v5Z"/></svg>';
const PLAY = '<svg viewBox="0 0 10 12" width="9" height="11" aria-hidden="true"><path d="M1 1l8 5-8 5Z" fill="currentColor"/></svg>';
const MINUS = '<svg viewBox="0 0 20 20" width="100%" height="100%" aria-hidden="true"><path d="M5 10h10" fill="none" stroke="#ffe7a8" stroke-width="2.6" stroke-linecap="round"/></svg>';
const AUTO_BTN = 'flex:0 0 auto;display:flex;align-items:center;gap:5px;box-sizing:border-box;padding:0 9px 0 11px;border:1px solid rgba(242,193,78,.7);border-radius:999px;' +
  'background:rgba(10,16,32,.6);color:#ffe7a8;font-weight:800;font-family:' + FONT + ';white-space:nowrap;';
const AUTO_ON = 'background:linear-gradient(180deg,#f7d57a,#c98c22);color:#2a1a04;box-shadow:0 0 10px rgba(242,193,78,.8),0 2px 0 rgba(0,0,0,.45);';

function keyOf(a: QuestAction | null): string {
  return !a ? '' : a.kind === 'npc' ? 'npc:' + a.role : a.kind === 'door' ? 'door:' + a.region : a.kind === 'gate' ? 'gate' : '';
}
function requestAuto(a: AutoRequest): void {
  window.dispatchEvent(new CustomEvent(AUTO_QUEST_EVENT, { detail: a }));
}
/** 누를 수 있는 칸: 클릭·Enter·Space로 동작하고, 키보드 포커스 표시를 남긴다 */
function pressable(e: HTMLElement, label: string, run: () => void): void {
  e.style.pointerEvents = 'auto';
  e.style.cursor = 'pointer';
  e.style.touchAction = 'manipulation';
  e.setAttribute('role', 'button');
  e.setAttribute('aria-label', label);
  e.tabIndex = 0;
  e.addEventListener('pointerdown', (ev) => ev.preventDefault());
  e.addEventListener('click', run);
  e.addEventListener('keydown', (ev) => {
    if (ev.code !== 'Enter' && ev.code !== 'NumpadEnter' && ev.code !== 'Space') return;
    ev.preventDefault(); ev.stopPropagation(); run();
  });
}

export class VillageQuestCard {
  private readonly layout: TrackerLayout = newTrackerLayout();
  private readonly root: HTMLDivElement;
  /** 아이콘 모드 단추 */
  private readonly minBtn: HTMLDivElement;
  private readonly crest: HTMLImageElement;
  private readonly dot: HTMLDivElement;
  /** 한 줄 추적기 */
  private readonly line: HTMLDivElement;
  private readonly icon: HTMLDivElement;
  private readonly textBox: HTMLDivElement;
  private readonly nameEl: HTMLDivElement;
  private readonly subEl: HTMLDivElement;
  private readonly subChange: HTMLSpanElement;
  private readonly subDetail: HTMLSpanElement;
  /** 「모두 장착」: 더 좋은 장비를 부위별로 한 번에 낀다(장착 목표일 때만) */
  private readonly equipAllBtn: HTMLDivElement;
  private readonly equipBreak: HTMLDivElement;
  private readonly equipAllText: HTMLSpanElement;
  private readonly autoBtn: HTMLDivElement;
  private readonly autoText: HTMLSpanElement;
  private readonly shrinkBtn: HTMLDivElement;
  /** 오늘 현상금 칩(한 줄 모드, 추적기 아래 줄) */
  private readonly chip: HTMLDivElement;
  private readonly chipBreak: HTMLDivElement;
  private readonly badge: HTMLDivElement;
  private goalAction: QuestAction | null = null;
  private prefs: QuestCardPrefs = loadQuestCardPrefs();
  private readonly onPrefs = (e: Event): void => { this.prefs = (e as CustomEvent<QuestCardPrefs>).detail; this.dirty = true; };
  private dirty = true;
  private shown = false;
  private mode: TrackerMode | '' = '';
  private kActive = '-';
  private kGoal: QuestGoalView | null | undefined = undefined;
  private kTitle = '';
  /** 「✔ 장착 완료」 표시 중인지(바뀌면 다시 그린다) */
  private kTick = false;
  private kTown = -1;
  private kScale = -1;
  private kWidth = -1;
  private kTop = -1;
  private kViewH = -1;
  private kViewW = -1;
  private kAdv = '-';
  private bounty: BountyStatus | null = null;

  /** touch: 터치 기기(누르는 칸 44px 이상). onOpenWindow: 한 줄 추적기의 글자 부분을 눌렀을 때 퀘스트 창을 연다 */
  constructor(parent: HTMLElement, private readonly touch: boolean, private readonly onOpenWindow: () => void = () => {}) {
    this.root = el('div', 'position:absolute;left:' + CARD_LEFT + 'px;display:none;pointer-events:none;font-family:' + FONT + ';', parent);
    this.root.dataset.testid = 'village-quest';
    // 월드 이름표(NPC·시설)가 추적기 밑에 들어가 글자가 잘리지 않도록 화면 예약 영역으로 알린다(렌더 placeLabels)
    setPanelRect('village-quest', elementRect(this.root));
    window.addEventListener(QUEST_CARD_EVENT, this.onPrefs);
    installEquipFxStyle();

    // ── 아이콘 모드: 마을 문장 + 목표 있음 표시 점
    this.minBtn = el('div', 'position:relative;display:none;place-items:center;box-sizing:border-box;width:' + MIN_BUTTON + 'px;height:' + MIN_BUTTON + 'px;border:1px solid rgba(242,193,78,.65);border-radius:50%;' +
      'background:rgba(10,16,32,.62);', this.root);
    this.minBtn.dataset.testid = 'village-quest-min';
    this.crest = iconOver(el('div', 'width:36px;height:36px;display:grid;place-items:center;', this.minBtn), 'town-1', 32, STAR_SVG);
    this.dot = el('div', 'position:absolute;right:-1px;top:-1px;width:12px;height:12px;border-radius:50%;background:#f2c14e;border:2px solid #0d1328;box-shadow:0 0 6px rgba(242,193,78,.9);display:none;', this.minBtn);
    this.badge = el('div', 'position:absolute;right:-3px;bottom:-3px;min-width:16px;height:16px;padding:0 3px;box-sizing:border-box;border-radius:8px;background:#c0392b;border:1.5px solid #0d1328;color:#fff;font:800 11px/13px ' + FONT + ';text-align:center;display:none;pointer-events:none;', this.minBtn);
    this.badge.dataset.testid = 'village-bounty-badge';
    pressable(this.minBtn, '퀘스트 추적기 펴기', () => { toggleTracker(viewW(), viewH()); });
    this.minBtn.setAttribute('data-tip', '퀘스트 추적기 펴기');

    // ── 한 줄 추적기
    // HUD 정리(2026-10-27 시안 B): 상자 틀 없이 왼쪽에서 오른쪽으로 옅어지는 반투명 띠
    this.line = el('div', 'display:none;flex-wrap:wrap;align-items:center;box-sizing:border-box;border:0;border-radius:8px;' +
      'background:linear-gradient(90deg,rgba(8,12,24,.66) 0%,rgba(8,12,24,.58) 70%,rgba(8,12,24,0) 100%);', this.root);
    this.icon = el('div', 'flex:0 0 auto;box-sizing:border-box;display:grid;place-items:center;border:0;border-radius:50%;background:transparent;', this.line);
    // 글자 부분: 누르면 퀘스트 창
    this.textBox = el('div', 'flex:1 1 0;min-width:0;display:flex;flex-direction:column;gap:1px;line-height:1.25;', this.line);
    this.textBox.dataset.testid = 'village-quest-open';
    pressable(this.textBox, '퀘스트 창 열기', () => this.onOpenWindow());
    this.textBox.setAttribute('data-tip', '퀘스트 창 열기 (G)');
    this.nameEl = el('div', 'font-weight:800;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' + SHADOW, this.textBox, '');
    this.nameEl.dataset.testid = 'village-goal';
    this.subEl = el('div', 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:700;color:#dfe6ff;' + SHADOW, this.textBox);
    this.subChange = el('span', 'font-weight:900;color:' + C.gold + ';', this.subEl, '');
    this.subDetail = el('span', '', this.subEl, '');
    // 「모두 장착」: 장착 목표일 때만 보인다. 누르면 더 좋은 장비를 부위별로 한 번에 끼고 토스트가 요약한다
    this.equipBreak = el('div', 'flex:0 0 100%;height:0;order:8;display:none;', this.line);
    this.equipAllBtn = el('div', AUTO_BTN, this.line);
    this.equipAllText = el('span', '', this.equipAllBtn, '모두 장착');
    this.equipAllBtn.dataset.testid = 'village-equip-all';
    pressable(this.equipAllBtn, '추천 장비 모두 장착', () => { window.dispatchEvent(new CustomEvent(EQUIP_BEST_EVENT)); });
    this.equipAllBtn.setAttribute('data-tip', '더 좋은 장비를 한 번에 모두 장착');
    // 「자동」: 판매 NPC에게 걸어가 대화(구매 목표), 문으로 가기(기록 목표), 가방 열기(장착 목표)
    this.autoBtn = el('div', AUTO_BTN, this.line);
    this.autoText = el('span', '', this.autoBtn, '자동');
    iconOver(el('span', 'display:flex;width:16px;height:16px;', this.autoBtn), 'btn-auto', 18, PLAY);
    pressable(this.autoBtn, '다음 목표 자동 진행', () => { if (this.goalAction) requestAuto(this.goalAction); });
    this.autoBtn.dataset.testid = 'village-auto-goal';
    this.autoBtn.setAttribute('data-tip', '자동 진행 (Q)');
    // 오늘 현상금 칩: 맨 아래 줄(order 10). 누르면 퀘스트 창이 「모험」 탭으로 열린다
    this.chipBreak = el('div', 'flex:0 0 100%;height:0;order:10;display:none;', this.line);
    this.chip = el('div', 'order:11;display:none;', this.line);
    this.chip.dataset.testid = 'village-bounty-chip';
    pressable(this.chip, '오늘 현상금 보기', () => {
      window.dispatchEvent(new CustomEvent(QUEST_TAB_EVENT, { detail: 'adventure' }));
      this.onOpenWindow();
    });
    this.chip.setAttribute('data-tip', '오늘 현상금 (모험 탭)');
    this.shrinkBtn = el('div', 'flex:0 0 auto;box-sizing:border-box;border:1.5px solid #e2b24a;border-radius:50%;background:linear-gradient(180deg,#2c3a68,#121a36);box-shadow:0 2px 0 rgba(0,0,0,.45);', this.line);
    this.shrinkBtn.innerHTML = MINUS;
    this.shrinkBtn.dataset.testid = 'village-quest-toggle';
    pressable(this.shrinkBtn, '퀘스트 추적기 접기', () => { toggleTracker(viewW(), viewH()); });
    this.shrinkBtn.setAttribute('data-tip', '추적기 접기');
  }

  /** 진행 중인 자동 목표의 버튼을 금색으로 칠하고 「진행 중」으로 바꾼다 */
  private paintActive(): void {
    const active = autoActive.key;
    if (active === this.kActive) return;
    this.kActive = active;
    const key = keyOf(this.goalAction);
    const on = key !== '' && key === active;
    this.autoBtn.style.cssText = AUTO_BTN + this.autoSizeCss + 'pointer-events:auto;cursor:pointer;touch-action:manipulation;' + (this.goalAction ? '' : 'display:none;') + (on ? AUTO_ON : '');
    this.autoText.textContent = on ? '진행 중' : this.goalAction?.kind === 'bag' ? '가방' : '자동';
  }
  private autoSizeCss = '';
  private equipAllCss = '';
  private kScaleNow = 1;

  /** 배율이 바뀔 때만 크기·글자를 다시 정한다(글자는 화면 기준 12px 이상, 터치 칸은 44px 이상) */
  private applySizes(k: number, width: number): void {
    const px = (n: number): string => Math.round(n * k) + 'px';
    const font = (n: number): string => Math.max(MIN_BODY_PX, Math.round(n * k * 10) / 10) + 'px';
    const btnH = Math.max(Math.round(28 * k), this.touch ? MIN_BUTTON : 26);
    this.line.style.width = width + 'px';
    this.line.style.gap = px(8);
    this.line.style.padding = px(6) + ' ' + px(8) + ' ' + px(6) + ' ' + px(7);
    const ic = Math.round(38 * k);
    this.icon.style.width = this.icon.style.height = ic + 'px';
    this.nameEl.style.font = '800 ' + font(15) + ' ' + FONT;
    this.subEl.style.font = '700 ' + font(13) + ' ' + FONT;
    this.kScaleNow = k;
    this.autoSizeCss = 'height:' + btnH + 'px;font-size:' + font(14) + ';';
    this.equipAllCss = AUTO_BTN + this.autoSizeCss + 'pointer-events:auto;cursor:pointer;touch-action:manipulation;border-color:rgba(125,255,160,.8);color:#d9ffe6;order:9;margin-left:' + Math.round(40 * this.kScaleNow) + 'px;';
    this.kActive = '-';
    this.paintActive();
    this.shrinkBtn.style.width = this.shrinkBtn.style.height = btnH + 'px';
    this.shrinkBtn.style.padding = Math.round(btnH * 0.26) + 'px';
    this.chip.style.height = chipHeight(k, this.touch) + 'px';
    this.chip.style.font = '800 ' + font(13) + ' ' + FONT;
    this.textBox.style.minHeight = this.touch ? MIN_BUTTON + 'px' : '';
    this.textBox.style.minWidth = this.touch ? '28px' : '0';
    this.textBox.style.justifyContent = 'center';
    this.drawIcon();
  }

  private drawIcon(): void {
    const g = this.kGoal;
    this.icon.textContent = '';
    if (!g) return;
    const ic = parseInt(this.icon.style.width || '38', 10);
    if (g.icon === 'item') {
      const img = el('img', 'width:88%;height:88%;object-fit:contain;filter:drop-shadow(0 2px 2px rgba(0,0,0,.7));', this.icon) as HTMLImageElement;
      img.alt = '';
      img.src = assetUrl(g.itemIcon);
    } else {
      const big = gateSvg(GATE_COLOR[g.tone], Math.round(ic * 0.72));
      // 지역 목표면 그 지역 엠블럼(UI 아트 패스), 없으면 성문 그림
      if (g.action?.kind === 'door') iconOver(this.icon, regionEmblem(g.action.region), Math.round(ic * 0.9), big);
      else this.icon.innerHTML = big;
    }
  }

  private drawGoal(g: QuestGoalView | null, title: string): void {
    this.goalAction = g?.action ?? null;
    const tick = this.kTick;
    // 장착 직후: 다음 목표로 넘어가기 전에 「✔ 장착 완료」와 바뀐 능력치를 잠깐 보여 준다(퀘스트 줄이 체크되는 느낌)
    if (tick) {
      this.nameEl.textContent = '';
      el('span', 'margin-right:6px;', this.nameEl, '✔').className = 'dh-eq-check';
      el('span', 'color:#7dffa0;', this.nameEl, '장착 완료 · ' + equipTick.name);
      this.subChange.textContent = equipTick.detail;
      this.subDetail.textContent = '';
      this.line.style.borderColor = '#5fe08e';
    } else if (g) {
      this.nameEl.textContent = g.name;
      this.subChange.textContent = g.change;
      this.subDetail.textContent = (g.change && g.detail ? ' · ' : '') + g.detail;
      this.line.style.borderColor = g.icon === 'gate' ? TIER_STYLE[g.tone].border : '#d9a441';
    } else {
      this.nameEl.textContent = title;
      this.subChange.textContent = '';
      this.subDetail.textContent = '퀘스트 창에서 지역 길을 고르세요';
      this.line.style.borderColor = '#d9a441';
    }
    const eqN = !tick && g ? g.equipCount : 0;
    this.equipAllBtn.style.cssText = this.equipAllCss + (eqN > 0 ? '' : 'display:none;');
    this.equipAllText.textContent = eqN > 1 ? '추천 장비 모두 장착 ' + eqN : '추천 장비 장착';
    this.equipBreak.style.display = eqN > 0 ? 'block' : 'none';
    // 장착 안내는 한 줄에 잘리지 않게 줄을 바꿔 보인다(누를 순서가 읽혀야 한다)
    this.subEl.style.whiteSpace = eqN > 0 || tick ? 'normal' : 'nowrap';
    this.dot.style.display = g ? 'block' : 'none';
    this.minBtn.setAttribute('aria-label', g ? '퀘스트 추적기 펴기 · 목표 ' + g.name : '퀘스트 추적기 펴기');
    this.kActive = '-';
    this.drawIcon();
    this.paintActive();
  }

  /** u: 디자인 배율. show: 마을 장면일 때만. goal: 성장 목표가 바뀔 때만 새 객체 */
  update(u: number, show: boolean, title: string, goal: QuestGoalView | null, town = 0, adv: AdventureView | null = null): void {
    // 설정에서 추적기를 끄면 마을이어도 그리지 않는다
    const on = show && this.prefs.visible;
    if (on !== this.shown) {
      this.shown = on;
      this.root.style.display = on ? 'block' : 'none';
      this.dirty = true;
    }
    if (!on) {
      if (villageHudLayout.bottom !== -1) { villageHudLayout.bottom = -1; villageHudLayout.rect = null; villageHudLayout.version++; }
      return;
    }
    const W = viewW(), H = viewH();
    if (town !== this.kTown) {
      this.kTown = town;
      const crest = town >= 1 ? townCrest(town) : null;
      if (crest) setUiIcon(this.crest, crest);
    }
    const mode = resolveTrackerMode(this.prefs.tracker, W, H);
    if (mode !== this.mode) {
      this.mode = mode;
      this.minBtn.style.display = mode === 'min' ? 'grid' : 'none';
      this.line.style.display = mode === 'line' ? 'flex' : 'none';
      this.dirty = true;
    }
    const lay = trackerLayout(u, W, H, this.layout, this.touch);
    const top = cardTopPx(u, W, H);
    if (lay.scale !== this.kScale || lay.width !== this.kWidth) {
      this.kScale = lay.scale; this.kWidth = lay.width;
      this.applySizes(lay.scale, lay.width);
      this.dirty = true;
    }
    const tickOn = performance.now() < equipTick.until;
    if (goal !== this.kGoal || title !== this.kTitle || tickOn !== this.kTick) {
      this.kGoal = goal; this.kTitle = title; this.kTick = tickOn;
      this.drawGoal(goal, title);
      this.dirty = true;
    }
    this.paintActive();
    // 현상금: 리비전·날짜·최고 마을이 바뀔 때만 다시 계산·그린다(프레임마다 할당 없음)
    const advKey = adv ? adv.revision + ':' + adv.day + ':' + adv.highestTown : '';
    if (advKey !== this.kAdv) {
      this.kAdv = advKey;
      this.bounty = bountyStatus(adv);
      this.drawBounty();
      this.dirty = true;
    }
    if (top !== this.kTop || W !== this.kViewW || H !== this.kViewH) {
      this.kTop = top; this.kViewW = W; this.kViewH = H;
      this.root.style.top = top + 'px';
      this.dirty = true;
    }
    if (this.dirty) {
      this.dirty = false;
      // 내용·배율이 바뀐 프레임에만 크기를 잰다(레이아웃 1회)
      const w = mode === 'min' ? MIN_BUTTON : this.line.offsetWidth, h = mode === 'min' ? MIN_BUTTON : this.line.offsetHeight;
      villageHudLayout.rect = { x: CARD_LEFT, y: top, w, h };
      villageHudLayout.bottom = Math.round(top + h);
      villageHudLayout.version++;
    }
  }

  private drawBounty(): void {
    const b = this.bounty;
    if (!b || b.total === 0) { this.chipBreak.style.display = 'none'; this.chip.style.display = 'none'; this.badge.style.display = 'none'; this.chip.textContent = ''; return; }
    const done = b.remaining === 0;
    this.chip.textContent = bountyChipText(b);
    this.chipBreak.style.display = 'block';
    this.chip.style.cssText += 'display:flex;align-items:center;box-sizing:border-box;padding:0 10px;border-radius:999px;flex:0 0 auto;margin-left:' + Math.round(45 * this.kScaleNow) + 'px;' +
      'border:1px solid ' + (done ? 'rgba(125,255,160,.7)' : 'rgba(242,193,78,.7)') + ';background:rgba(10,16,32,.6);color:' + (done ? '#aef5c3' : '#ffe7a8') + ';white-space:nowrap;' + SHADOW;
    this.badge.textContent = String(b.remaining);
    this.badge.style.display = done ? 'none' : 'block';
  }

  /** 시험·QA용: 지금 모드 */
  get currentMode(): TrackerMode | '' { return this.mode; }
}

/** 설정 창에서 쓰는 모드 고르기: 자동(화면으로 정함) / 한 줄 / 아이콘 하나 */
export function setTrackerPref(mode: TrackerMode | null): void { updateQuestCardPrefs({ tracker: mode }); }
